import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useBrand } from "@/contexts/BrandContext";
import { toast } from "sonner";
import { AUTH } from "@/constants/testIds";
import { Zap, Eye, EyeOff, AlertCircle } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.requires_2fa) {
        navigate("/verify-2fa", { state: { challenge_id: result.challenge_id } });
      } else {
        toast.success(`Welcome to ${brand.productName}`);
        navigate("/home");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Invalid credentials";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAF6F0] px-4">
      {/* Background grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(122,26,43,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(122,26,43,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />

      <div className="relative w-full max-w-md">
        {/* Brand */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-12 h-12 rounded bg-[#7A1A2B] flex items-center justify-center mb-4">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <h1 data-testid="brand-product-name" className="text-3xl font-bold text-[#181818] font-sans tracking-tight">{brand.productName}</h1>
          <p data-testid="brand-company-name" className="text-sm text-[#706E6B] mt-1 font-mono">Powered by {brand.companyName}</p>
          <p data-testid="brand-tagline" className="text-xs text-[#C9A15F] mt-2 text-center">{brand.primaryTagline}</p>
        </div>

        {/* Card */}
        <div className="bg-white border border-[#E7D8C0] rounded-lg p-8" style={{ boxShadow: "0 2px 8px rgba(122,26,43,0.08)" }}>
          <h2 className="text-lg font-semibold text-[#181818] font-sans mb-1">Sign in to your workspace</h2>
          <p className="text-sm text-[#706E6B] mb-6">Partner &amp; Team Login</p>

          <form data-testid={AUTH.loginForm} onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-1.5">Email</label>
              <input
                data-testid={AUTH.emailInput}
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="admin@prashasta.com"
                required
              className="w-full px-4 py-2.5 rounded border border-[#C9C9C9] bg-white text-[#181818] text-sm placeholder:text-[#C9C9C9] focus:outline-none focus:border-[#7A1A2B] focus:ring-1 focus:ring-[#C9A15F] transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-1.5">Password</label>
              <div className="relative">
                <input
                  data-testid={AUTH.passwordInput}
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full px-4 py-2.5 pr-10 rounded border border-[#C9C9C9] bg-white text-[#181818] text-sm placeholder:text-[#C9C9C9] focus:outline-none focus:border-[#7A1A2B] focus:ring-1 focus:ring-[#C9A15F] transition-colors"
                />
                <button type="button" onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#706E6B] hover:text-[#444444]">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div data-testid={AUTH.errorMsg} className="flex items-center gap-2 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-400 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <button
              data-testid={AUTH.submitBtn}
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded bg-[#7A1A2B] text-white font-semibold text-sm hover:bg-[#5E1220] transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-2"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>

            <div className="text-center mt-3">
              <Link
                to="/forgot-password"
                data-testid={AUTH.forgotPasswordLink}
                className="text-xs text-[#7A1A2B] hover:text-[#5E1220] hover:underline transition-colors"
              >
                Forgot your password?
              </Link>
            </div>
          </form>

          {/* Google OAuth — shown only when not in UAT_ROLE_PICKER mode */}
          <div className="mt-5">
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-[#E5E5E5]" />
              <span className="text-xs text-[#706E6B]">or</span>
              <div className="flex-1 h-px bg-[#E5E5E5]" />
            </div>
            <button
              data-testid="login-google-btn"
              type="button"
              onClick={async () => {
                try {
                  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/google/init`, { credentials: "include" });
                  const { oauth_url } = await res.json();
                  window.location.href = oauth_url;
                } catch {
                  toast.error("Could not initiate Google sign-in. Please try again.");
                }
              }}
              className="w-full flex items-center justify-center gap-3 py-2.5 px-4 rounded border border-[#D9D9D9] bg-white text-[#444] text-sm font-medium hover:bg-[#F8F8F8] hover:border-[#B0B0B0] transition-colors"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </button>
          </div>

          {/* Demo credentials — visible in development only */}
          {process.env.REACT_APP_ENVIRONMENT !== "UAT" && (
          <div className="mt-6 pt-4 border-t border-[#E5E5E5]">
            <p className="text-xs font-mono text-[#706E6B] text-center mb-2">Demo Credentials</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                ["Platform Admin", "gnreddymailcpa@gmail.com", "Admin@123"],
                ["Tenant Admin", "admin@prashasta.com", "Admin@123"],
                ["Setup Admin", "setup@prashasta.com", "Setup@123"],
                ["Approver", "approver@prashasta.com", "Approve@123"],
              ].map(([role, em, pw]) => (
                <button key={em} type="button" onClick={() => { setEmail(em); setPassword(pw); }}
                  className="text-left p-2 rounded-lg bg-[#FAF6F0] border border-[#E7D8C0] hover:border-[#7A1A2B]/40 transition-colors">
                  <p className="text-[10px] font-mono text-[#706E6B]">{role}</p>
                  <p className="text-[10px] text-[#706E6B] truncate">{em}</p>
                </button>
              ))}
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
